/**
 * The month: six weeks of days. Each day shows its first few things; pick a
 * day to see all of it underneath. On a phone the days show coloured dots.
 */
import { cn } from "@/lib/utils";
import { DAY_SHORT, WEEK_ORDER, fromYmd } from "../lib";
import type { CalItem } from "../types";
import { CalChip, itemDot } from "./CalChip";
import { sortDay } from "./WeekGrid";

export function MonthGrid({
  days, month, items, today, selected, onSelect, onOpen,
}: {
  days: string[];
  /** YYYY-MM of the month being shown (other days are dimmed). */
  month: string;
  items: CalItem[];
  today: string;
  selected: string | null;
  onSelect: (day: string) => void;
  onOpen: (it: CalItem) => void;
}) {
  const MAX = 3;
  return (
    <div className="cc-card overflow-hidden">
      <div className="grid grid-cols-7 border-b border-zinc-800/70">
        {WEEK_ORDER.map((d) => (
          <div key={d} className="px-2 py-2 text-center text-[11px] font-semibold uppercase tracking-wider text-zinc-500 sm:text-left">
            <span className="sm:hidden">{DAY_SHORT[d]!.slice(0, 1)}</span>
            <span className="hidden sm:inline">{DAY_SHORT[d]}</span>
          </div>
        ))}
      </div>
      <div className="grid grid-cols-7">
        {days.map((d, i) => {
          const list = items.filter((x) => x.date === d);
          const { untimed, timed } = sortDay(list);
          const ordered = [...untimed, ...timed];
          const inMonth = d.startsWith(month);
          const isToday = d === today;
          const isSel = d === selected;
          return (
            <div
              key={d}
              role="button"
              tabIndex={0}
              onClick={() => onSelect(d)}
              onKeyDown={(e) => { if (e.key === "Enter") onSelect(d); }}
              className={cn(
                "flex min-h-14 min-w-0 cursor-pointer flex-col gap-0.5 border-zinc-800/60 p-1 transition-colors hover:bg-zinc-800/20 sm:min-h-[112px] sm:p-1.5",
                i % 7 !== 6 && "border-r",
                i < days.length - 7 && "border-b",
                !inMonth && "bg-zinc-950/30",
                isSel && "bg-indigo-500/10 ring-1 ring-inset ring-indigo-400/30",
              )}
            >
              <div className="flex items-center justify-center sm:justify-start">
                <span className={cn(
                  "flex h-6 min-w-6 items-center justify-center rounded-full px-1 text-xs tabular-nums",
                  isToday ? "cc-brand-bg font-semibold text-white" : inMonth ? "text-zinc-200" : "text-zinc-600",
                )}>
                  {fromYmd(d).getDate()}
                </span>
              </div>
              {/* Phone: dots */}
              <div className="flex flex-wrap justify-center gap-0.5 sm:hidden">
                {ordered.slice(0, 6).map((it) => <span key={it.id} className={cn("h-1.5 w-1.5 rounded-full", itemDot(it), (it.ghost || it.done) && "opacity-40")} />)}
              </div>
              {/* Wider: titles */}
              <div className="hidden min-w-0 flex-col gap-px sm:flex">
                {ordered.slice(0, MAX).map((it) => <CalChip key={it.id} it={it} onOpen={onOpen} compact />)}
                {ordered.length > MAX && <span className="px-1 text-[10px] font-medium text-zinc-500">+{ordered.length - MAX} more</span>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
